import type { TopHoldingsDataResponse } from 'venn-api';
import { useMemo } from 'react';
import type { ColDef } from 'ag-grid-community';
import { createLabelColumn } from '../../../../studio-blocks/logic/columnParsers';
import { getComparisonLabelForBlockLegend } from 'venn-utils';
import HeaderCellRenderer from '../../../../studio-blocks/components/grid/renderers/HeaderCellRenderer';
import { getDefaultCellClass, getDefaultValueFormat } from '../../../../studio-blocks/logic/gridStyling';
import ValueCellRenderer from '../../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { useRecoilValue } from 'recoil';
import { blockLimitedHoldingsSubjectsWithFees } from 'venn-state';
import type { TopHoldingsRow } from '../../../../studio-blocks/types';

const TOTAL_ROW_ID = 'total-row';

export const useTopHoldingsTable = (blockId: string, data: TopHoldingsDataResponse) => {
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(blockId));

  const rowData: TopHoldingsRow[] | undefined = useMemo(() => {
    return data.topHoldings[0]?.holdings.map(({ assetName, weight }) => ({
      id: assetName,
      key: assetName,
      label: assetName,
      type: 'PERCENTAGE',
      weight,
    }));
  }, [data]);

  const pinnedBottomRowData: TopHoldingsRow[] = useMemo(
    () => [
      {
        id: TOTAL_ROW_ID,
        key: TOTAL_ROW_ID,
        label: 'Total %MV in Top 10 Holdings',
        type: 'PERCENTAGE',
        weight: rowData?.length ? rowData?.map((row) => row.weight).reduce((a, b) => a + b) : 0,
      },
    ],
    [rowData],
  );

  const columnDefs = useMemo((): ColDef<TopHoldingsRow>[] => {
    return [
      createLabelColumn({}),
      {
        headerName: getComparisonLabelForBlockLegend(allSubjects[0]!),
        headerComponent: HeaderCellRenderer,
        headerComponentParams: {
          subject: allSubjects[0]!,
        },
        valueGetter: (row) => row.data?.weight,
        valueFormatter: getDefaultValueFormat,
        cellRenderer: ValueCellRenderer,
        cellClass: getDefaultCellClass,
        autoHeight: false,
      },
    ];
  }, [allSubjects]);

  return { rowData, columnDefs, pinnedBottomRowData };
};
